import React, { useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { Framerate } from '../models/Timeline';

interface PlayheadProps {
  currentTime: number;
  timelineScale: number;
  trackHeight: number;
  onDrag: (time: number) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  fps?: Framerate;
  showFrames?: boolean;
  offsetLeft?: number; // Offset from left edge to account for labels
}

export const Playhead = observer(({ 
  currentTime, 
  timelineScale, 
  trackHeight,
  onDrag,
  onDragStart,
  onDragEnd,
  fps = Framerate.FPS_30,
  showFrames = false,
  offsetLeft = 0
}: PlayheadProps) => {
  const playheadRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  
  // Update playhead position when time or scale changes
  useEffect(() => {
    if (playheadRef.current && !isDragging) {
      const position = offsetLeft + (currentTime * timelineScale);
      playheadRef.current.style.left = `${position}px`;
    }
  }, [currentTime, timelineScale, isDragging, offsetLeft]);
  
  // Handle mouse down on playhead
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setStartX(e.clientX);
    onDragStart();
    
    // Add document-level event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Add throttling to avoid excessive updates during drag
  const lastDragUpdateRef = useRef<number>(0);
  const throttleInterval = 50; // ms between updates
  
  // Handle mouse move during drag with throttling
  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && playheadRef.current) {
      const now = Date.now();
      const deltaX = e.clientX - startX;
      const currentLeft = offsetLeft + (currentTime * timelineScale);
      const newPosition = Math.max(offsetLeft, currentLeft + deltaX);
      const newTime = (newPosition - offsetLeft) / timelineScale;
      
      // Always update visual position for smooth UI
      playheadRef.current.style.left = `${newPosition}px`;
      
      // Throttle the actual time updates to reduce renderer load events
      if (now - lastDragUpdateRef.current > throttleInterval) {
        // Call onDrag with new time
        onDrag(newTime);
        lastDragUpdateRef.current = now;
      }
      
      // Update start position for next move
      setStartX(e.clientX);
    }
  };
  
  // Handle mouse up to end dragging
  const handleMouseUp = () => {
    setIsDragging(false);
    onDragEnd();
    
    // Remove document-level event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Calculate current frame
  const currentFrame = Math.round(currentTime * fps);
  
  // Format time display
  const formatTimeDisplay = () => {
    const minutes = Math.floor(currentTime / 60);
    const seconds = Math.floor(currentTime % 60);
    
    if (showFrames) {
      const frames = Math.floor((currentTime % 1) * fps);
      return `${minutes}:${seconds.toString().padStart(2, '0')}:${frames.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  };

  return (
    <PlayheadContainer 
      ref={playheadRef} 
      style={{ 
        height: `${trackHeight}px`,
        left: `${offsetLeft + currentTime * timelineScale}px`
      }}
      onMouseDown={handleMouseDown}
    >
      <PlayheadHead />
      <PlayheadLine />
      {showFrames && (
        <FrameDisplay>
          {formatTimeDisplay()}
        </FrameDisplay>
      )}
    </PlayheadContainer>
  );
});

const PlayheadContainer = styled.div`
  position: absolute;
  top: 0;
  z-index: 100;
  cursor: ew-resize;
  pointer-events: auto;
`;

const PlayheadHead = styled.div`
  position: absolute;
  top: 0;
  left: -8px;
  width: 16px;
  height: 16px;
  background-color: #FF3D71;
  border-radius: 0 0 8px 8px;
  transform: translateY(-2px);
`;

const PlayheadLine = styled.div`
  position: absolute;
  top: 14px;
  left: 0;
  width: 1px;
  height: calc(100% - 14px);
  background-color: #FF3D71;
`;

const FrameDisplay = styled.div`
  position: absolute;
  top: 16px;
  left: 4px;
  padding: 2px 6px;
  background-color: rgba(255, 61, 113, 0.8);
  color: white;
  font-size: 10px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
`;